import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface EmailCampaign {
  id: number;
  campaign_id: string;
  email_type: string;
  subject_line: string;
  html_body: string;
  tips_date: string;
  status: 'draft' | 'ready' | 'sent' | 'archived';
  brevo_list_ids: number[];
  estimated_recipients: number;
  metadata: { brevoCampaignId?: number; testPassedAt?: string; sendHistory?: unknown[]; totalSends?: number };
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  sent_by: string | null;
}

const BASE = '/brevo/email-campaigns';

// Renders the branded header/footer wrapper the send path applies, so a preview
// matches what recipients receive. Stateless (takes live editor content), so
// both the tips-email and marketing-campaign previews use it. `enabled` gates
// it to when a preview is actually shown and there's content to wrap.
export function useEmailPreview(subject: string, htmlBody: string, enabled: boolean) {
  return useQuery({
    queryKey: ['email-preview', subject, htmlBody],
    queryFn: () =>
      apiClient<{ subject: string; html: string }>(`${BASE}/preview`, {
        method: 'POST',
        body: JSON.stringify({ subject, htmlBody }),
      }),
    enabled: enabled && htmlBody.trim().length > 0,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['email-campaigns'] });
}

export function useEmailCampaigns() {
  return useQuery({
    queryKey: ['email-campaigns'],
    queryFn: async () => (await apiClient<{ campaigns: EmailCampaign[] }>(BASE)).campaigns,
  });
}

// n8n authors the draft, but the admin still needs to correct a subject or a
// stray bit of HTML before sending. The API has always supported this (it also
// clears metadata.testPassedAt so an edit forces a fresh test send); only the
// dialog was read-only.
export function useUpdateEmailCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; subject_line?: string; html_body?: string }) =>
      apiClient<{ campaign: EmailCampaign }>(`${BASE}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useSendTestEmailCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, testEmails, listIds }: { id: number; testEmails: string[]; listIds: number[] }) =>
      apiClient<{ testPassedAt: string; campaign: EmailCampaign }>(`${BASE}/${id}/send-test`, {
        method: 'POST',
        body: JSON.stringify({ testEmails, listIds }),
      }),
    onSuccess: invalidate,
  });
}

export function useSendEmailCampaign() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, listIds, overrideSent }: { id: number; listIds: number[]; overrideSent?: boolean }) =>
      apiClient<{ campaign: EmailCampaign }>(`${BASE}/${id}/send`, {
        method: 'POST',
        body: JSON.stringify({ listIds, confirmSend: true, overrideSent }),
      }),
    onSuccess: invalidate,
  });
}

export function useEmailCampaignRecipientCount(id: number, listIds: number[]) {
  return useQuery({
    queryKey: ['email-campaign-recipient-count', id, listIds],
    queryFn: () => apiClient<{ count: number }>(`${BASE}/${id}/recipient-count?listIds=${listIds.join(',')}`),
    enabled: listIds.length > 0,
  });
}
